if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
        this.giftCardValues = {};
      }

      restoreCustomGiftCardAmount() {
        const customAmountInput = document.getElementById('customAmount');
        if (!customAmountInput) return;

        const savedAmount = this.giftCardValues['custom_amount'];

        // Check if we have a saved state (including empty string)
        if (savedAmount !== undefined) {
          // If saved amount is empty or whitespace, explicitly clear the field
          if (!savedAmount || !savedAmount.trim()) {
            customAmountInput.value = '';
            customAmountInput.parentNode.classList.remove('has-value');
            // Remove the hidden custom amount property
            const productForm = this.querySelector('form');
            if (productForm) {
              const existingInput = productForm.querySelector('.js-custom-properties-value');
              if (existingInput) {
                existingInput.remove();
              }
            }
          } else {
            // Restore non-empty saved amount
            const numericAmount = parseFloat(savedAmount);
            // Read min and max from HTML attributes (single source of truth).
            // Use attribute value when valid (including 0); otherwise fallback (same rule as addCustomAmountProperties).
            const minParsed = parseFloat(customAmountInput.getAttribute('min'));
            const maxParsed = parseFloat(customAmountInput.getAttribute('max'));
            const minAmount = Number.isNaN(minParsed) ? 0.01 : minParsed;
            const maxAmount = Number.isNaN(maxParsed) ? 500 : maxParsed;

            // Only restore if the saved amount is within valid range
            if (!Number.isNaN(numericAmount) && numericAmount >= minAmount && numericAmount <= maxAmount) {
              customAmountInput.value = savedAmount;
              customAmountInput.parentNode.classList.add('has-value');
              // Directly recreate hidden property instead of dispatching event
              this.addCustomAmountProperties(numericAmount);
            }
          }
        }
      }

      /**
       * Adds/updates the hidden custom gift card amount property.
       * NOTE: This logic is duplicated in src/js/lib/product-gift-card.js
       * Keep both implementations in sync if changes are needed.
       * @param {number} amountValue - The custom amount value
       */
      addCustomAmountProperties(amountValue) {
        let minAmount = 0.01;
        let maxAmount = 500;
        const customAmountInput = document.getElementById('customAmount');
        if (customAmountInput) {
          const min = parseFloat(customAmountInput.getAttribute('min'));
          const max = parseFloat(customAmountInput.getAttribute('max'));
          if (!Number.isNaN(min)) minAmount = min;
          if (!Number.isNaN(max)) maxAmount = max;
        }
        if (typeof amountValue !== 'number' || Number.isNaN(amountValue) || amountValue < minAmount || amountValue > maxAmount) {
          return;
        }

        const productForm = this.querySelector('form');
        if (!productForm) return;

        const existingInput = productForm.querySelector('.js-custom-properties-value');

        if (existingInput) {
          // Update existing custom properties input
          existingInput.value = amountValue;
        } else {
          // Create custom properties input if it doesn't exist
          const customProperties = document.createElement('input');
          customProperties.setAttribute('type', 'hidden');
          customProperties.setAttribute('name', 'properties[_custom-gc-amount]');
          customProperties.setAttribute('value', amountValue);
          customProperties.classList.add('js-custom-properties-value');
          productForm.appendChild(customProperties);
        }
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true, composed: true }));
        this.classList.add('is-loaded');

        const variantJson = document.querySelector('[data-selected-variant]');
        const initialVariant = variantJson ? JSON.parse(variantJson.textContent) : null;
        this.toggleSupportedDevices(initialVariant);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        // Check for custom amount input, do not reset product info
        if (event.target.id === 'customAmount') return;
        if (!this.contains(event.target)) return;

        // --- Preserve gift card form values ---
        const productForm = this.productForm;
        const giftCardForm = productForm?.querySelector('.js-gift-card-form');

        if (giftCardForm) {
          // Save all input and textarea values
          giftCardForm.querySelectorAll('input, textarea').forEach((el) => {
            this.giftCardValues[el.name] = el.value;
          });
        }

        // Preserve custom gift card amount input
        const customAmountInput = document.getElementById('customAmount');
        if (customAmountInput) {
          this.giftCardValues['custom_amount'] = customAmountInput.value.trim();
        }


        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";

          const productInfoNode = html.querySelector(selector);

          if (!productInfoNode) {
            console.warn("[handleSwapProduct] product-info not found for selector:", selector);
            return;
          }

          const variant = this.getSelectedVariant(productInfoNode);

          if (updateFullPage) {
            this.updateSeoTitle(html);
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          // --- Restore gift card form values ---
          setTimeout(() => {
            const productForm = this.productForm;
            const giftCardForm = productForm?.querySelector('.js-gift-card-form');
            if (giftCardForm && this.giftCardValues) {
              giftCardForm.querySelectorAll('input, textarea').forEach((el) => {
                if (this.giftCardValues[el.name] !== undefined) {
                  if (this.giftCardValues[el.name]) {
                    el.parentNode.classList.add('has-value');
                  }
                  el.value = this.giftCardValues[el.name];
                  el.dispatchEvent(new Event('input', { bubbles: true }));
                }
              });
            }

            // Restore custom gift card amount input
            this.restoreCustomGiftCardAmount();
          }, 0);
          // --- End restore ---
          const updateProductInfo = this.handleUpdateProductInfo(productUrl);
          updateProductInfo(html);

          this.toggleSupportedDevices(variant);

          if (window.reinitSeeAllFormatDrawer) {
            window.reinitSeeAllFormatDrawer(variant?.id?.toString() || '');
          }

          if (window.handleFormatDisplay) {
            window.handleFormatDisplay();
          }

          if (window.addProductFulfillmentBlockEvents) {
            window.addProductFulfillmentBlockEvents();
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            const el = document.getElementById(targetId);
            if (!el) return;
            try {
              el.focus({ preventScroll: true });
            } catch (e) {
              // Fallback for older browsers that don't support preventScroll
              el.focus();
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getSelectedVariantDescription(productInfoNode) {
        // Use shared utility function for consistency with product-overview.js
        const utilityFn = window.ProductVariantUtils?.getSelectedVariantDescription;
        if (utilityFn) {
          // Pass productInfoNode as root for scoped query
          return utilityFn(productInfoNode);
        }
        // Fallback implementation if utility not loaded - with error handling
        const selectedVariantDescription = productInfoNode.querySelector('variant-selects [data-selected-variant-description]')?.innerHTML;
        if (!selectedVariantDescription) {
          return null;
        }
        try {
          return JSON.parse(selectedVariantDescription);
        } catch (e) {
          console.warn('[ProductInfo] Failed to parse variant description JSON (fallback):', e);
          return null;
        }
      }

      getSelectedVariantUpc(productInfoNode) {
        const selectedVariantUpc = productInfoNode.querySelector('variant-selects [data-selected-variant-upc]')?.innerHTML;
        return !!selectedVariantUpc ? JSON.parse(selectedVariantUpc) : null;
      }

      getSelectedVariantFinalSale(productInfoNode) {
        const selectedVariantFinalSale = productInfoNode.querySelector('variant-selects [data-selected-variant-final-sale]')?.dataset.selectedVariantFinalSale;
        return !!selectedVariantFinalSale ? JSON.parse(selectedVariantFinalSale) : null;
      }

      getSelectedVariantOversized(productInfoNode) {
        const selectedVariantOversized = productInfoNode.querySelector('variant-selects [data-selected-variant-oversized]')?.dataset.selectedVariantOversized;
        return !!selectedVariantOversized ? JSON.parse(selectedVariantOversized) : null;
      }

      getSelectedVariantOverweight(productInfoNode) {
        const selectedVariantOverweight = productInfoNode.querySelector('variant-selects [data-selected-variant-overweight]')?.dataset.selectedVariantOverweight;
        return !!selectedVariantOverweight ? JSON.parse(selectedVariantOverweight) : null;
      }

      getSelectedVariantPlumMeta(productInfoNode) {
        const selectedVariantPlumMeta = productInfoNode.querySelector('variant-selects [data-selected-variant-plum-meta]')?.innerHTML;
        return !!selectedVariantPlumMeta ? JSON.parse(selectedVariantPlumMeta) : null;
      }

      getProductTitle(productInfoNode) {
        return productInfoNode.querySelector('.product__title')?.innerText || '';
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      parseCurrencyAmount(rawValue) {
        // Keep this parser behavior aligned with src/js/util/money-localization.js
        // (parseStandaloneMoneyValue + related normalization/regex helpers).
        const normalizedText = String(rawValue ?? '')
          .replace(/[\u00A0\u202F]/g, ' ')
          .trim();
        const standaloneMoneyRegex = /^-?\s*(?:[A-Z]{1,3}\$?|[$€£])?\s*(?:\d{1,3}(?:[ \u00A0\u202F.,]\d{3})+|\d+)(?:[.,]\d+)?\s*(?:[$€£]|[A-Z]{3})?\s*$/i;
        if (!standaloneMoneyRegex.test(normalizedText)) return NaN;

        const compact = normalizedText.replace(/\s/g, '');
        const numeric = compact.replace(/[^0-9,.-]/g, '');
        if (!numeric) return NaN;

        const hasComma = numeric.includes(',');
        const hasDot = numeric.includes('.');

        if (hasComma && hasDot) {
          const lastComma = numeric.lastIndexOf(',');
          const lastDot = numeric.lastIndexOf('.');
          const normalized = lastComma > lastDot
            ? numeric.replace(/\./g, '').replace(',', '.')
            : numeric.replace(/,/g, '');
          return Number(normalized);
        }

        if (hasComma) {
          const normalized = /,\d{1,2}$/.test(numeric)
            ? numeric.replace(/\./g, '').replace(',', '.')
            : numeric.replace(/,/g, '');
          return Number(normalized);
        }

        return Number(numeric.replace(/,/g, ''));
      }

      localizeMoneyPrices(rootNode = this) {
        const htmlLanguage = (document.documentElement.lang || '').toLowerCase();
        if (!htmlLanguage.startsWith('fr')) return;

        if (!rootNode) return;
        const priceNodes = rootNode.querySelectorAll('.js-money-price');
        if (!priceNodes.length) return;

        const formatter = new Intl.NumberFormat('fr-CA', {
          style: 'currency',
          currency: 'CAD',
          currencyDisplay: 'narrowSymbol',
        });

        priceNodes.forEach((node) => {
          if (!node || node.children.length > 0) return;
          const amount = this.parseCurrencyAmount(node.textContent);
          if (!Number.isFinite(amount)) return;
          node.textContent = formatter.format(amount);
        });
      }

      localizeVariantOptionPrices() {
        this.localizeMoneyPrices(this.variantSelectors || this);
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          this.localizeVariantOptionPrices();
        }
      }

      updateContributors(html) {
        this.querySelectorAll('[data-pdp-contributors]').forEach((destination) => {
          const block = destination.dataset.pdpContributors;
          const source = html.querySelector(`[data-pdp-contributors="${block}"]`);

          if (source) {
            destination.replaceWith(source.cloneNode(true));
          } else {
            destination.innerHTML = '';
            destination.classList.add('hide');
          }
        });
      }

      updateSeoTitle(html) {
        if (this.dataset.updateUrl !== 'true') return;

        const source = html.querySelector('product-info[data-seo-title]');
        const title = source?.getAttribute('data-seo-title') ?? html.querySelector('head title')?.textContent;
        if (!title?.trim()) return;

        document.title = title;
        ['meta[property="og:title"]', 'meta[name="twitter:title"]'].forEach((selector) => {
          document.querySelector(selector)?.setAttribute('content', title);
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id, html);
          if (!variant) {
            this.setUnavailable();
            // Dispatch fallback event for media gallery when variant is unavailable
            // Find a variant that matches the first selected option
            this.dispatchColorFallback();
            return;
          }

          this.updateSeoTitle(html);
          this.updateMedia(html, variant?.featured_media?.id);
          this.updateContributors(html);

          // Update Constructor.io tracking attributes on variant change
          this.updateConstructorTracking(variant);

          // --- Restore gift card form values ---
          setTimeout(() => {
            const productForm = this.productForm;
            const giftCardForm = productForm?.querySelector('.js-gift-card-form');
            if (giftCardForm && this.giftCardValues) {
              giftCardForm.querySelectorAll('input, textarea').forEach((el) => {
                if (this.giftCardValues[el.name] !== undefined) {
                  if (this.giftCardValues[el.name]) {
                    el.parentNode.classList.add('has-value');
                  }
                  el.value = this.giftCardValues[el.name];
                  el.dispatchEvent(new Event('input', { bubbles: true }));
                }
              });
            }

            // Restore custom gift card amount input
            this.restoreCustomGiftCardAmount();
          }, 0);
          // --- End restore ---
          // Get variant UPC for external sections update
          const variantUPC = this.getSelectedVariantUpc(html);
          // Update overview description on variant change using shared updater
          const variantDescription = this.getSelectedVariantDescription(html);
          const overviewInstance = window.getProductOverviewInstance?.();
          if (overviewInstance && typeof overviewInstance.updateOverviewDescription === 'function') {
            overviewInstance.updateOverviewDescription(variantDescription, variant?.id);
          }
          // Update final sale visibility on variant change
          const finalSale = this.getSelectedVariantFinalSale(html);
          const oversized = this.getSelectedVariantOversized(html);
          const overweight = this.getSelectedVariantOverweight(html);
          const finalSaleElement = this.querySelector('.product__final-sale');
          const oversizedElement = this.querySelector('.product__oversized');
          const overweightElement = this.querySelector('.product__overweight');

          if (finalSaleElement) {
            if (finalSale) {
              finalSaleElement.classList.remove('hide');
            } else {
              finalSaleElement.classList.add('hide');
            }
          }

          if (oversizedElement) {
            if (oversized) {
              oversizedElement.classList.remove('hide');
            } else {
              oversizedElement.classList.add('hide');
            }
          }

          if (overweightElement) {
            if (overweight) {
              overweightElement.classList.remove('hide');
            } else {
              overweightElement.classList.add('hide');
            }
          }

          const productTitle = this.getProductTitle(html);
          const productTitleElements = this.querySelectorAll('.product__title');
          if (productTitleElements.length && productTitle) {
            productTitleElements.forEach(element => {
              element.innerText = productTitle;
            });
          }

          const plumNonEligible = this.getSelectedVariantPlumMeta(html);
          const rewardsBlock = this.querySelector('.product-rewards-block');
          if (rewardsBlock) {
            if (plumNonEligible) {
              rewardsBlock.classList.add('hidden');
            } else {
              rewardsBlock.classList.remove('hidden');
            }
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false, replaceOuterHtml = false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (!source || !destination) return;

            if (replaceOuterHtml) {
              const clone = source.cloneNode(true);
              clone.classList.toggle('hidden', shouldHide(source));
              destination.replaceWith(clone);
              return;
            }

            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('hidden', shouldHide(source));
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('addToBagModalTrigger');
          updateSourceFromDestination('addToBagModal', () => false, true);
          updateSourceFromDestination('productMinOrderQtyMessage', ({ classList }) => classList.contains('hidden'));

          this.querySelectorAll('[data-supported-devices]').forEach((destinationBlock) => {
            const blockId = destinationBlock.dataset.blockId;
            const sourceBlock = html.querySelector(`[data-supported-devices][data-block-id="${blockId}"]`);

            if (!sourceBlock) {
            destinationBlock.setAttribute('hidden', '');
            destinationBlock.classList.add('hide');
            destinationBlock.innerHTML = '';
            return;
          }

            destinationBlock.replaceWith(sourceBlock.cloneNode(true));
          });

        this.toggleSupportedDevices(variant);

        // Update external sections that depend on variant changes
        this.updateExternalSections(productUrl, variant, variantUPC);

          const $formBtns = html.querySelector('.js-form-buttons');
          const $formBtnsDestination = this.querySelector('.js-form-buttons');
          const $formBtnsSkeleton = html.querySelector('.js-form-buttons-skeleton');
          const $formBtnsSkeletonDestination = this.querySelector('.js-form-buttons-skeleton');
          this.syncElementContent($formBtnsSkeleton, $formBtnsSkeletonDestination);
          this.syncElementContent($formBtns, $formBtnsDestination);

          const sourceStickyAtc = html.getElementById('stickyATC');
          const destinationStickyAtc = document.getElementById('stickyATC');
          this.syncElementContent(sourceStickyAtc, destinationStickyAtc);
          window.syncStickyQuantityVisibility?.();
          window.preserveAllKoboOutboundButtons?.();

          const sourceFulfillment = html.querySelector('.js-fulfillment-container');
          const destinationFulfillment = this.querySelector('.js-fulfillment-container');
          if (sourceFulfillment && destinationFulfillment) {
            const destinationIsHidden = _.hasClass(destinationFulfillment, 'hide');
            const destinationChecked = destinationFulfillment.querySelector('input[name="properties[fulfillment_method]"]:checked');
            const currentMethod = destinationChecked?.value || 'ship';

            // Preserve the last *visible* fulfillment method so digital variants (which hide the
            // fulfillment UI) don't keep an invisible "pickup" selection that can disable ATC.
            if (!destinationIsHidden && currentMethod) {
              destinationFulfillment.dataset.lastVisibleFulfillmentMethod = currentMethod;
            }

            if (_.hasClass(sourceFulfillment, 'hide')) {
              _.addClass(destinationFulfillment, 'hide');
            } else {
              _.removeClass(destinationFulfillment, 'hide');
            }

            destinationFulfillment.innerHTML = sourceFulfillment.innerHTML;

            const sourceIsHidden = _.hasClass(sourceFulfillment, 'hide');
            const lastVisibleMethod = destinationFulfillment.dataset.lastVisibleFulfillmentMethod;
            const preferredMethod = sourceIsHidden ? 'ship' : (lastVisibleMethod || currentMethod || 'ship');

            const applyCheckedMethod = (method) => {
              const input = destinationFulfillment.querySelector(`input[value="${method}"]`);
              if (!input) return null;
              input.checked = true;
              return method;
            };

            const resolvedMethod = applyCheckedMethod(preferredMethod)
              || applyCheckedMethod('ship')
              || applyCheckedMethod('pickup')
              || 'ship';

            if (resolvedMethod === 'pickup') {
              const shipContent = destinationFulfillment.querySelector('[data-method="ship"]');
              const pickupContent = destinationFulfillment.querySelector('[data-method="pickup"]');

              if (shipContent) shipContent.classList.add('hide');
              if (pickupContent) pickupContent.classList.remove('hide');
            }

            if (window.reinitProductFulfillmentBlock) {
              window.reinitProductFulfillmentBlock();
            }
          }

          // Handle digital message visibility for ebook/Kobo audiobook variants
          const sourceDigitalMessage = html.querySelector('.js-digital-message');
          const destinationDigitalMessage = this.querySelector('.js-digital-message');
          if (sourceDigitalMessage && destinationDigitalMessage) {
            if (_.hasClass(sourceDigitalMessage, 'hide')) {
              _.addClass(destinationDigitalMessage, 'hide');
            } else {
              _.removeClass(destinationDigitalMessage, 'hide');
            }
            // Update content in case the message changed
            destinationDigitalMessage.innerHTML = sourceDigitalMessage.innerHTML;
          }

          const destinationReviewsReleaseDate = this.querySelector('.product__reviews-release-date');
          if (destinationReviewsReleaseDate) {
            const existingReleaseDateMeta = destinationReviewsReleaseDate.querySelector('.product__release-date-meta');
            if (existingReleaseDateMeta) existingReleaseDateMeta.remove();
            const sourceReleaseDateMeta = html.querySelector('.product__reviews-release-date .product__release-date-meta');
            if (sourceReleaseDateMeta) {
              destinationReviewsReleaseDate.appendChild(sourceReleaseDateMeta.cloneNode(true));
            }
          }

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          if (window.addProductFulfillmentBlockEvents) {
            window.addProductFulfillmentBlockEvents(this);
          }

          // Update promotions on variant change
          const sourcePromos = html.querySelector('.product__promotions');
          const destinationPromos = this.querySelector('.product__promotions');
          if (sourcePromos && destinationPromos) {
            destinationPromos.innerHTML = sourcePromos.innerHTML;
            sourcePromos.classList.contains('hide')
              ? destinationPromos.classList.add('hide')
              : destinationPromos.classList.remove('hide');
          }

          // Sync notification-banners wrapper visibility (so promos show when switching to a variant that has them)
          const sourceNotificationBanners = html.querySelector('.product__notification-banners');
          const destinationNotificationBanners = this.querySelector('.product__notification-banners');
          if (sourceNotificationBanners && destinationNotificationBanners) {
            sourceNotificationBanners.classList.contains('hide')
              ? destinationNotificationBanners.classList.add('hide')
              : destinationNotificationBanners.classList.remove('hide');
          }

          // Update promo modal content
          const sourceModal = html.getElementById('promo-modal');
          const destinationModal = document.getElementById('promo-modal');
          if (sourceModal && destinationModal) {
            destinationModal.innerHTML = sourceModal.innerHTML;
          }

          if (window.reinitPromotionModal) {
            window.reinitPromotionModal();
          }

          // Variant swaps replace price blocks in the DOM; re-apply FR formatting on new nodes.
          this.localizeMoneyPrices(this);
          const $stickyAtc = document.getElementById('stickyATC');
          if ($stickyAtc) this.localizeMoneyPrices($stickyAtc);
          window.syncStickyQuantityVisibility?.();

          // Dispatch a custom event to notify about the change
          this.dispatchEvent(
            new CustomEvent('variant:change', {
              bubbles: true,
              composed: true,
              detail: {
                variant: variant,
                el: this,
              },
            })
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      /**
       * Update Constructor.io tracking attributes when variant changes
       * @param {Object} variant - The selected variant object
       */
      updateConstructorTracking(variant) {
        if (!variant) return;
        this.setAttribute('data-cnstrc-item-variation-id', String(variant.id));
        this.setAttribute('data-cnstrc-item-price', (Number(variant.price) * 0.01).toFixed(2));
      }

      updateVariantInputs(variantId, html) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));

          // Use the actual id attribute. In forms, a field named "id" can shadow
          // the `.id` property with the input element itself.
          const productFormId = productForm.getAttribute('id');
          const escapedFormId = (typeof CSS !== 'undefined' && typeof CSS.escape === 'function')
            ? CSS.escape(productFormId || '')
            : (productFormId || '');
          const sourceForm = escapedFormId ? html?.querySelector?.(`#${escapedFormId}`) : null;
          const syncHiddenLineProperty = (propertyName) => {
            const destinationInput = productForm.querySelector(`input[name="${propertyName}"]`);
            if (!destinationInput) return;

            const sourceInput = sourceForm?.querySelector?.(`input[name="${propertyName}"]`);
            destinationInput.value = sourceInput?.value || '';
          };

          syncHiddenLineProperty('properties[_original_list_price]');
          syncHiddenLineProperty('properties[_promotions]');
          syncHiddenLineProperty('properties[_product_title]');
          syncHiddenLineProperty('properties[_cio_item_id]');
          syncHiddenLineProperty('properties[_cio_variation_id]');
          const priceIdName = 'properties[_priceid]';
          const sourcePriceId = sourceForm?.querySelector(`input[name="${priceIdName}"]`);
          const destinationPriceId = productForm.querySelector(`input[name="${priceIdName}"]`);
          if (sourcePriceId && destinationPriceId) {
            destinationPriceId.value = sourcePriceId.value;
          } else if (sourcePriceId) {
            productForm.appendChild(sourcePriceId.cloneNode(true));
          } else {
            destinationPriceId?.remove();
          }
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        if (url.includes('?')) url = url.split('?')[0];
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      dispatchColorFallback() {
        const firstOption = this.variantSelectors?.querySelector('fieldset input:checked, select option[selected]');
        if (!firstOption) return;

        const selectedValue = (firstOption.value || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-') // replace non-alphanumeric with hyphens
          .replace(/-+/g, '-') // collapse multiple hyphens
          .replace(/^-|-$/g, ''); // trim leading/trailing hyphens
        if (!selectedValue) return;

        const gallery = document.querySelector('media-gallery');
        if (!gallery) return;

        const variantSlides = gallery.querySelectorAll('[data-variant-slider-group]');
        for (const slide of variantSlides) {
          const option = slide.dataset.variantOption || '';
          if (option.startsWith(selectedValue + '-') || option === selectedValue) {
            document.dispatchEvent(new CustomEvent('variant:color-fallback', {
              detail: { variantId: slide.dataset.variantSliderGroup }
            }));
            return;
          }
        }
      }

      isDigitalVariant(variant) {
        if (variant) {
          const variantTitle = (variant.title || '').toLowerCase();
          const tokenEbook = (this.dataset.tokenEbook || '').toLowerCase();
          const tokenKoboAudiobook = (this.dataset.tokenKoboAudiobook || '').toLowerCase();
          if (
            (tokenEbook && variantTitle.includes(tokenEbook)) ||
            (tokenKoboAudiobook && variantTitle.includes(tokenKoboAudiobook))
          ) {
            return true;
          }

          const ebookMetafield =
            variant?.metafields?.indigo?.ebookid ||
            variant?.metafields?.indigo?.ebook_id ||
            variant?.metafields?.indigo?.ebookId;
          if (ebookMetafield) {
            return true;
          }

          return false;
        }
        // Fallback: check DOM presence
        return !!this.querySelector('.product__digital-message');
      }

      toggleSupportedDevices(variant = null) {
        const isDigital = this.isDigitalVariant(variant);
        this.querySelectorAll('[data-supported-devices]').forEach((block) => {
          const hasContent = !!block.querySelector('.product__supported-device, .product__supported-devices-title, .product__supported-devices-footnote');

          if (isDigital && hasContent) {
            block.classList.remove('hide');
            block.removeAttribute('hidden');
          } else {
            block.classList.add('hide');
            block.setAttribute('hidden', '');
          }
        });
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
          false
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        const selectors = ['.quantity__input'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      /**
       * Re-initializes accordion functionality for a section
       * @param {HTMLElement} section - The section element to reinitialize
       */
      reinitializeAccordion(section) {
        const accordionWrapper = section.querySelector('[id^="accordion-as-section-"]');

        if (accordionWrapper) {
          const accordionId = accordionWrapper.id;
          const openByDefault = accordionWrapper.dataset?.openByDefault === 'true';

          try {
            if (window.useSectionAsAccordion) {
              // Use the high-level function if available
              window.useSectionAsAccordion({
                dom: {
                  id: accordionId,
                },
                open_option: openByDefault ? 0 : null,
                reset_on_resize: false,
              });
            } else if (window.SDG && window.SDG.accordion) {
              // Fallback to direct accordion initialization
              const accordionInstance = window.SDG.accordion({
                dom: {
                  id: accordionId,
                },
                open_option: openByDefault ? 0 : null,
                reset_on_resize: false,
              });
              accordionInstance.init();
            }
          } catch (error) {
            console.error('Accordion initialization failed:', error);
          }
        }
      }

      /**
       * Syncs element content and hide class from source to destination
       * @param {HTMLElement} source - The source element
       * @param {HTMLElement} destination - The destination element
       */
      syncElementContent(source, destination) {
        if (!source || !destination) return;

        destination.innerHTML = source.innerHTML;
        if (_.hasClass(source, 'hide')) {
          _.addClass(destination, 'hide');
        } else {
          _.removeClass(destination, 'hide');
        }
      }

      /**
       * Updates external sections that depend on variant changes
       * @param {string} productUrl - The product URL
       * @param {Object} variant - The selected variant object
       */
      async updateExternalSections(productUrl, variant, variantUPC) {
        // Configuration for sections that need to be updated on variant change
        const sectionsToUpdate = [
          {
            sectionSelector: 'section.product-details',
            name: 'product-details',
            callback: this.reinitializeAccordion.bind(this)
          },
          {
            sectionSelector: 'section.rich-text-multicolumn__parent',
            name: 'rich-text-multicolumn-variant-metafields',
            callback: this.reinitializeAccordion.bind(this)
          }
        ];

        // Process all configured sections concurrently
        await Promise.all(
          sectionsToUpdate.map((config) =>
            this.updateSingleExternalSection(config, productUrl, variant, variantUPC)
          )
        );
      }

      /**
       * Updates a single external section
       * @param {Object} config - Section configuration
       * @param {string} productUrl - The product URL
       * @param {Object} variant - The selected variant object
       */
      async updateSingleExternalSection(config, productUrl, variant, variantUPC) {
        try {
          // Find ALL sections on the page that match the selector
          const sections = document.querySelectorAll(config.sectionSelector);
          if (!sections.length) return;

          // Process each section individually
          for (const section of sections) {
            // Only refresh rich-text sections that are variant-metafield driven.
            if (config?.name === 'rich-text-multicolumn-variant-metafields') {
              if (!section.querySelector('[data-uses-variant-metafields="true"]')) continue;
            }

            // If the section is product-details, update the UPC field
            if (section.classList.contains('product-details')) {
              const upcField = section.querySelector('.js-product-details__metafield--upc');
              if (upcField) {
                upcField.innerText = variantUPC || '';
              }
            }

            // Extract section ID from the section element and clean it for request param
            const rawSectionId = section.id;
            if (!rawSectionId) continue;
            const cleanedSectionId = rawSectionId.replace('shopify-section-', '');

            // Build URL to fetch the section with variant parameters
            const urlParams = new URLSearchParams();
            if (variant?.id) urlParams.set('variant', variant.id);
            urlParams.set('section_id', cleanedSectionId);
            // `productUrl` may already include query params (e.g. ?variant=...).
            // Always fetch sections from the base path to avoid malformed URLs like `...?variant=...?variant=...&section_id=...`.
            const baseProductUrl = (productUrl || '').split('?')[0];
            const sectionUrl = `${baseProductUrl}?${urlParams.toString()}`;

            // Fetch the updated section
            const response = await fetch(sectionUrl);
            if (!response.ok) {
              console.error(`HTTP ${response.status}: ${response.statusText} for section ${rawSectionId}`);
              continue;
            }
            const responseText = await response.text();

            // Parse the response and update the specified content
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = responseText;

            // Find the new section (Shopify renders with the raw DOM id prefix)
            const newSection = tempDiv.querySelector(`section#${rawSectionId}`) ||
              tempDiv.querySelector(`section#shopify-section-${cleanedSectionId}`);

            if (newSection && section) {
              // Normalize publisher fields before accordion measurements and insertion.
              window.normalizePublisherContent?.(newSection);

              // Replace the entire section
              section.parentNode?.replaceChild(newSection, section);

              // Execute callback if provided
              if (config.callback && typeof config.callback === 'function') {
                try {
                  config.callback(newSection);
                } catch (callbackError) {
                  console.error(`${config.name} callback failed for section ${rawSectionId}:`, callbackError);
                }
              }
            } else {
              console.warn(`Could not find content selectors for ${config.name} section ${rawSectionId}`);
            }
          }
        } catch (error) {
          console.error(`Failed to update ${config.name}:`, error);
        }
      }
    }
  );
}
